// מודל שיחה בצד לקוח (11 §1–2): היסטוריה מהשרת + אירועי SSE חיים, עם lastSeq בזיכרון בלבד.
// כל אירוע עובר applyEvent — אותה פונקציה להיסטוריה ול-SSE, ו-seq ≤ lastSeq נזרק (replay בלי כפילויות).
// הודעה באמצע תור (10 §1): send בזמן תור חי → השרת מכניס אותה לתור (אותו turnId + sayId); היא מופיעה מיד בתמליל
// (חלק 'user' במקום שנשלחה; האירוע user_message מהשרת מאחד לפי sayId), ו-heard מסמן שהסוכן קיבל אותה.
// שליחה = "תיבת יציאה" (outbox): ההודעה מוצגת מיד כבועה ממתינה, נשלחת ברקע לפי הסדר, ומתאחדת עם התמליל כשהשרת אישר
// (תשובת ה-POST, או אירוע queued/user_message עם ה-clientId שלה). כשל — הבועה נשארת עם השגיאה ו"שליחה שוב"; רשת/deploy —
// ניסיון חוזר אוטומטי (אותו clientId — השרת לא שולח פעמיים); 409 (התור נעצר/מסתיים/מלא) — ממתינה ונשלחת כשהתור מסתיים.
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { aiApi } from './aiApi.ts';
import { parseSseEvent, type Attachment } from './aiContract.ts';
import { fromHistory, isLive, reducer, type TurnView } from './conversationModel.ts';
import { ApiError } from './api.ts';
import { openSse, type SseState } from './sse.ts';
import { T } from './strings.ts';
import { retryDelayMs, TRANSIENT_STATUSES } from './updating.ts';

export { isLive, type Part, type TurnView } from './conversationModel.ts';

/** הודעה שיצאה מהמסך ועוד לא אושרה בשרת (10 §1) — מוצגת מיד; לעולם לא נעלמת בשקט. */
export type Outgoing = {
  /** גם ה-clientId שנשלח לשרת (uuid) */
  localId: string;
  /** null — שיחה חדשה (נוצרת בשליחה) */
  convId: string | null;
  /** השיחה שבה נכתבה (null — מסך "שיחה חדשה") — הבועה מוצגת שם עד הניווט לשיחה שנוצרה */
  origin: string | null;
  text: string;
  /** הקבצים שנבחרו (להחזרה לשדה ב"עריכה") */
  allFiles: File[];
  /** ...ומהם — אלה שעוד לא הועלו; uploaded — שהועלו (ניסיון חוזר לא מעלה שוב) */
  files: File[];
  uploaded: Attachment[];
  /** pending — בתור לשליחה; sending — בדרך; retrying — רשת/deploy, ניסיון חוזר בקרוב; waiting — 409, אחרי התור הנוכחי; failed — נכשלה */
  state: 'pending' | 'sending' | 'retrying' | 'waiting' | 'failed';
  errorHe: string | null;
  /** התקדמות ההעלאה (0..1) כשיש קבצים */
  progress: number | null;
  tries: number;
};

/** 409 שמשמעו "עוד רגע" — התור החי נעצר/מסתיים/מלא (או שרת בלי inbox) — לא כשל. */
const WAIT_KINDS = new Set(['turn_ending', 'inbox_full', 'turn_in_progress']);
const MAX_TRIES = 6;
function newId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16)); // http שאינו localhost — בלי randomUUID
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export type ConversationHook = {
  turns: TurnView[];
  /** הודעות שיצאו מהמסך ועוד לא אושרו (של השיחה הזו) — מוצגות אחרי התמליל */
  outbox: Outgoing[];
  loading: boolean;
  error: unknown;
  stream: SseState;
  /** תור חי שנעצר (stopping) — אין לאן לשלוח. תור חי שרץ אינו busy: אפשר לכתוב גם עכשיו (10 §1). */
  busy: boolean;
  /** יש תור חי (queued/running/stopping). */
  live: boolean;
  /** סודות (tool_result secret) — בזיכרון של הרכיב בלבד, לא ב-state, לא ב-storage; נעלמים בטעינה מחדש. */
  secrets: Map<string, string>;
  canContinue: boolean;
  /** שליחה: ההודעה נכנסת מיד לתיבת היציאה (מוצגת), הקבצים מועלים ואז ההודעה נשלחת. בזמן תור חי — נכנסת אליו (10 §1). */
  send: (text: string, files?: File[]) => void;
  /** הודעה שנכשלה — שליחה שוב (אותו clientId) */
  retry: (localId: string) => void;
  /** "עריכה"/"ביטול": ההודעה יוצאת מתיבת היציאה וחוזרת למי שקרא (לשדה) */
  takeBack: (localId: string) => { text: string; files: File[] } | null;
  stop: () => Promise<void>;
};

export function useConversation(
  level: 1 | 2,
  conversationId: string | undefined,
  opts: {
    onCreated: (id: string, text: string) => void;
    onConsentPending: (id: string) => void;
    /** 20: אין Gmail לכלי מייל בתור החי — המסך פותח "חיבור Gmail" (רק אירוע חי, לא היסטוריה). */
    onMailConnect?: (caps: Array<'send' | 'read'>) => void;
    /** קונסנט הוכרע (גם במכשיר/לשונית אחרים, או אחרי שהתור נגמר) — המגירה והרשימה מתעדכנות, לא נשארות "ממתין לאישור". */
    onConsentDecided?: (id: string) => void;
    onTurnDone?: () => void;
    onToolResult?: (name: string, ok: boolean) => void;
    /** בלוק חי (SSE, לא היסטוריה) עם focus:'expand' מאייבי — המסך פותח אותו בהגדלה; ב-replay לא נקרא. */
    onExpandBlock?: () => void;
  },
): ConversationHook {
  const [s, dispatch] = useReducer(reducer, { turns: [], lastSeq: 0 });
  const [loading, setLoading] = useState(!!conversationId);
  const [error, setError] = useState<unknown>(null);
  const [stream, setStream] = useState<SseState>('closed');
  const [canContinue, setCanContinue] = useState(true);
  const secrets = useRef(new Map<string, string>()).current;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const convRef = useRef(conversationId);
  convRef.current = conversationId;

  // תיבת היציאה: ref (לולאת השליחה קוראת ממנו) + state (לרינדור)
  const outRef = useRef<Outgoing[]>([]);
  const [outbox, setOutbox] = useState<Outgoing[]>([]);
  const patch = useCallback((localId: string, p: Partial<Outgoing> | null) => {
    const cur = outRef.current;
    if (!cur.some((m) => m.localId === localId)) return;
    outRef.current = p === null ? cur.filter((m) => m.localId !== localId) : cur.map((m) => (m.localId === localId ? { ...m, ...p } : m));
    setOutbox(outRef.current);
  }, []);

  useEffect(() => {
    dispatch({ t: 'reset', turns: [], lastSeq: 0 });
    setError(null);
    setCanContinue(true); // שיחה שנסגרה לא נועלת את השיחה הבאה
    if (!conversationId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let handle: ReturnType<typeof openSse> | null = null;
    setLoading(true);
    aiApi
      .history(conversationId)
      .then((h) => {
        if (cancelled) return;
        const init = fromHistory(h);
        dispatch({ t: 'reset', ...init });
        setCanContinue(h.conversation.canContinue);
        setLoading(false);
        handle = openSse(`/api/conversations/${encodeURIComponent(conversationId)}/stream`, {
          lastEventId: String(h.lastSeq),
          onState: setStream,
          onFatal: (e) => setError(e),
          onMessage: (m) => {
            const seq = Number(m.id);
            let raw: unknown;
            try {
              raw = JSON.parse(m.data);
            } catch {
              return;
            }
            const ev = parseSseEvent(raw);
            if (!ev || !Number.isFinite(seq)) return;
            let secretKey: string | undefined;
            if (ev.type === 'tool_result' && ev.secret) {
              secretKey = `${conversationId}:${seq}`;
              secrets.set(secretKey, ev.summaryHe);
            }
            dispatch({ t: 'event', seq, ...(ev.turnId ? { turnId: ev.turnId } : {}), ev, ...(secretKey ? { secretKey } : {}) });
            // השרת כבר קלט הודעה מתיבת היציאה (גם אם תשובת ה-POST עוד לא הגיעה / אבדה) — היא בתמליל, הבועה יורדת
            if ((ev.type === 'queued' || ev.type === 'user_message') && ev.clientId) patch(ev.clientId, null);
            if (ev.type === 'consent_pending') optsRef.current.onConsentPending(ev.consentId);
            if (ev.type === 'mail_connect') optsRef.current.onMailConnect?.(ev.caps);
            if (ev.type === 'consent_decided') optsRef.current.onConsentDecided?.(ev.consentId);
            if (ev.type === 'tool_result') optsRef.current.onToolResult?.(ev.name, ev.ok);
            if (ev.type === 'block' && ev.focus === 'expand') optsRef.current.onExpandBlock?.();
            if (ev.type === 'done') optsRef.current.onTurnDone?.();
          },
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
      handle?.close();
    };
  }, [conversationId, secrets, patch]);

  const live = s.turns.find(isLive);
  const liveRef = useRef(false);
  liveRef.current = !!live;

  // לולאת השליחה: אחת בכל פעם, לפי הסדר
  const sendingRef = useRef(false);
  const pump = useCallback(async () => {
    if (sendingRef.current) return;
    const m = outRef.current.find((x) => x.state === 'pending');
    if (!m) return;
    sendingRef.current = true;
    patch(m.localId, { state: 'sending', errorHe: null });
    const created = !m.convId;
    try {
      // שיחה חדשה: נוצרת קודם, כדי שיהיה לאן להעלות (ונשמרת — ניסיון חוזר לא יוצר שיחה שנייה)
      const convId = m.convId ?? (await aiApi.createConversation(level)).id;
      if (created) patch(m.localId, { convId });
      const uploaded = [...m.uploaded];
      const rest = [...m.files];
      const total = m.allFiles.length || 1;
      while (rest.length) {
        const done = uploaded.length;
        uploaded.push(await aiApi.uploadFile(convId, rest[0]!, (p) => patch(m.localId, { progress: (done + p) / total })));
        rest.shift();
        patch(m.localId, { uploaded: [...uploaded], files: [...rest] });
      }
      const r = await aiApi.sendTurn(convId, m.text, uploaded.map((a) => a.id), m.localId);
      patch(m.localId, null);
      if (created) optsRef.current.onCreated(convId, m.text);
      else if (convId === convRef.current) {
        // תור חי: ההודעה נכנסה אליו — במקום שנשלחה (האירוע מהשרת מאחד לפי sayId); אחרת תור חדש
        if (r.sayId) dispatch({ t: 'said', id: r.turnId, sayId: r.sayId, text: m.text, attachments: uploaded });
        else dispatch({ t: 'addTurn', id: r.turnId, userText: m.text, attachments: uploaded });
      }
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'network', T.network);
      const tries = m.tries + 1;
      const again = (state: Outgoing['state']) => {
        patch(m.localId, { state, tries, progress: null });
        setTimeout(() => {
          if (outRef.current.find((x) => x.localId === m.localId)?.state !== state) return;
          patch(m.localId, { state: 'pending' });
          void pump();
        }, retryDelayMs(tries - 1, null));
      };
      if (err.status === 409 && WAIT_KINDS.has(err.kind)) {
        // תור חי — נשלחת כשהוא מסתיים (האפקט למטה); אין תור חי בעינינו (התור בתהליך אחר / done עוד בדרך) — שוב בעוד רגע
        if (liveRef.current) patch(m.localId, { state: 'waiting', tries: 0, progress: null });
        else if (tries < MAX_TRIES * 2) again('waiting');
        else patch(m.localId, { state: 'failed', errorHe: err.textHe, progress: null });
      } else if ((err.status === 0 || TRANSIENT_STATUSES.has(err.status)) && tries < MAX_TRIES) again('retrying');
      else patch(m.localId, { state: 'failed', errorHe: err.textHe || T.network, progress: null });
    } finally {
      sendingRef.current = false;
      void pump();
    }
  }, [level, patch]);

  // הודעות שממתינות לסוף התור — כשאין תור חי, לשליחה
  useEffect(() => {
    if (live) return;
    let any = false;
    outRef.current = outRef.current.map((m) => (m.state === 'waiting' && m.convId === conversationId ? ((any = true), { ...m, state: 'pending' as const }) : m));
    if (any) {
      setOutbox(outRef.current);
      void pump();
    }
  }, [live, conversationId, pump]);

  const send = useCallback(
    (text: string, files: File[] = []) => {
      const t = text.trim();
      if (!t && !files.length) return;
      const m: Outgoing = { localId: newId(), convId: conversationId ?? null, origin: conversationId ?? null, text: t, allFiles: files, files, uploaded: [], state: 'pending', errorHe: null, progress: files.length ? 0 : null, tries: 0 };
      outRef.current = [...outRef.current, m];
      setOutbox(outRef.current);
      void pump();
    },
    [conversationId, pump],
  );

  const retry = useCallback(
    (localId: string) => {
      patch(localId, { state: 'pending', tries: 0, errorHe: null });
      void pump();
    },
    [patch, pump],
  );

  const takeBack = useCallback(
    (localId: string) => {
      const m = outRef.current.find((x) => x.localId === localId);
      if (!m || m.state === 'sending') return null;
      patch(localId, null);
      return { text: m.text, files: m.allFiles };
    },
    [patch],
  );

  const stop = useCallback(async () => {
    if (!live || live.status === 'stopping') return;
    dispatch({ t: 'stopping', id: live.id });
    try {
      await aiApi.cancelTurn(live.id);
    } catch (e) {
      setError(e);
    }
    // הקלט משתחרר רק כשמגיע done {canceled} מהשרת — לא כאן (11 §1)
  }, [live]);

  return {
    turns: s.turns,
    outbox: outbox.filter((m) => m.origin === (conversationId ?? null) || (conversationId != null && m.convId === conversationId)),
    loading,
    error,
    stream,
    busy: live?.status === 'stopping',
    live: !!live,
    secrets,
    canContinue,
    send,
    retry,
    takeBack,
    stop,
  };
}
